import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';
import { debounce } from 'lodash';

import { FORTA_BLUE, FORTA_GRAY3, FORTA_GRAY5 } from '../../utils/colors';
import FHConfirmDialog from './dialogs/FHConfirmDialog';
import TrashIcon from '../svgs/TrashIcon';

import type { Moment } from 'moment';

enum TimeUpdateAction {
  ADD = 'ADD',
  SUBTRACT = 'SUBTRACT',
}

interface FHDurationTagProps {
  id: number;
  duration: number;
  durationLimitInSeconds?: number;
  containerStyles?: React.CSSProperties;
  debounceTime?: number;
  disabled?: boolean;
  onDelete: (id: number) => void;
  onUpdate: (duration: number) => void;
}

function FHDurationTag({
  id,
  duration,
  durationLimitInSeconds = 8 * 3600, // 8 hours default
  containerStyles = {},
  debounceTime = 500,
  disabled = false,
  onDelete,
  onUpdate,
}: FHDurationTagProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [durationStartTime, _setDurationStartTime] = useState<Moment>(() => moment());
  const [durationEndTime, setDurationEndTime] = useState<Moment>(() =>
    moment().add(duration, 'seconds'),
  );
  const [endTimeLimit, setEndTimeLimit] = useState<Moment>(() =>
    moment().add(duration + durationLimitInSeconds, 'seconds'),
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedOnUpdate = useCallback(
    debounce((newEndTime: Moment) => {
      if (moment.isMoment(newEndTime)) {
        onUpdate(moment(newEndTime).diff(durationStartTime, 'seconds'));
      }
    }, debounceTime),
    [durationStartTime],
  );

  useEffect(() => {
    setEndTimeLimit(durationEndTime.clone().add(durationLimitInSeconds, 'seconds'));
  }, [durationLimitInSeconds]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangeHour = (action: TimeUpdateAction) => {
    let updatedTime = durationEndTime.clone();

    if (action === TimeUpdateAction.ADD) {
      updatedTime.add(1, 'hour');
      if (updatedTime.isAfter(endTimeLimit)) {
        return;
      }
    } else {
      if (updatedTime.diff(durationStartTime, 'seconds') <= 1) {
        return;
      }
      updatedTime.subtract(1, 'hour');
      const newDuration = updatedTime.diff(durationStartTime, 'seconds');
      if (newDuration <= 0) {
        updatedTime = durationStartTime.clone().add(1, 'seconds');
      }
    }
    setDurationEndTime(updatedTime);
    debouncedOnUpdate(updatedTime);
  };

  const handleChangeMinutes = (action: TimeUpdateAction) => {
    let updatedTime = durationEndTime.clone();
    if (action === TimeUpdateAction.ADD) {
      updatedTime.add(1, 'minute');
      // const newDuration = updatedTime.diff(durationStartTime, 'seconds');
      if (updatedTime.isAfter(endTimeLimit)) {
        return;
      }
    } else {
      if (updatedTime.diff(durationStartTime, 'seconds') <= 1) {
        return;
      }
      updatedTime.subtract(1, 'minute');
      const newDuration = updatedTime.diff(durationStartTime, 'seconds');
      if (newDuration <= 0) {
        updatedTime = durationStartTime.clone().add(1, 'seconds');
      }
    }
    setDurationEndTime(updatedTime);
    debouncedOnUpdate(updatedTime);
  };

  const handleChangeSeconds = (action: TimeUpdateAction) => {
    let updatedTime = durationEndTime.clone();
    if (action === TimeUpdateAction.ADD) {
      updatedTime.add(1, 'second');
      if (updatedTime.isAfter(endTimeLimit)) {
        return;
      }
    } else {
      if (updatedTime.diff(durationStartTime, 'seconds') <= 1) {
        return;
      }
      updatedTime.subtract(1, 'second');
      const newDuration = updatedTime.diff(durationStartTime, 'seconds');
      if (newDuration < 1) {
        updatedTime = durationStartTime.clone().add(1, 'seconds');
      }
    }
    setDurationEndTime(updatedTime);
    debouncedOnUpdate(updatedTime);
  };

  const durationVal = durationEndTime.diff(durationStartTime);

  return (
    <>
      <Box
        data-testid={`fh-duration-tag-${id}`}
        sx={{
          backgroundColor: FORTA_BLUE.light3,
          width: 222,
          borderRadius: '4px',
          py: isEditing ? 0.5 : 1,
          px: isEditing ? 0.8 : 2,
          '&:hover': disabled
            ? {}
            : {
                cursor: 'pointer',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
              },
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...containerStyles,
        }}
        ref={containerRef}
        onClick={() => {
          if (disabled) return;
          setIsEditing(true);
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Hours */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeHour(TimeUpdateAction.ADD);
                }}
                disabled={durationEndTime.clone().add(1, 'hour').isAfter(endTimeLimit)}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Box>
              <Typography
                color={FORTA_GRAY5}
                fontSize={24}
                display="inline"
                width={31}
                textAlign="center"
              >
                {moment.utc(durationVal).format('HH')}
              </Typography>
              <Typography color={FORTA_GRAY5} sx={{ ml: 0.4 }} display="inline" textAlign="center">
                h
              </Typography>
            </Box>
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeHour(TimeUpdateAction.SUBTRACT);
                }}
                disabled={durationEndTime
                  .clone()
                  .subtract(1, 'hour')
                  .isSameOrBefore(durationStartTime)}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          <Typography fontSize={24}>:</Typography>

          {/* Minutes */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMinutes(TimeUpdateAction.ADD);
                }}
                disabled={durationEndTime.clone().add(1, 'minute').isAfter(endTimeLimit)}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Box>
              <Typography
                color={FORTA_GRAY5}
                fontSize={24}
                width={31}
                display="inline"
                textAlign="center"
              >
                {moment.utc(durationVal).format('mm')}
              </Typography>
              <Typography color={FORTA_GRAY5} sx={{ ml: 0.4 }} display="inline" textAlign="center">
                m
              </Typography>
            </Box>
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeMinutes(TimeUpdateAction.SUBTRACT);
                }}
                disabled={durationEndTime
                  .clone()
                  .subtract(1, 'minute')
                  .isSameOrBefore(durationStartTime)}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          <Typography fontSize={24}>:</Typography>

          {/* Seconds */}
          <Box
            sx={{
              '& > .MuiTypography-root': {
                transition: 'all 0.2s ease-in-out',
              },
              '&:hover': {
                '& > .MuiTypography-root': {
                  fontWeight: isEditing ? 'bold' : 'normal',
                  color: isEditing ? FORTA_BLUE.primary : FORTA_GRAY5,
                },
              },
            }}
          >
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeSeconds(TimeUpdateAction.ADD);
                }}
                disabled={durationEndTime.clone().add(1, 'second').isAfter(endTimeLimit)}
              >
                <KeyboardArrowUp />
              </IconButton>
            )}
            <Box>
              <Typography
                color={FORTA_GRAY5}
                fontSize={24}
                width={31}
                display="inline"
                textAlign="center"
              >
                {moment.utc(durationVal).format('ss')}
              </Typography>
              <Typography color={FORTA_GRAY5} display="inline" sx={{ ml: 0.4 }} textAlign="center">
                s
              </Typography>
            </Box>
            {isEditing && moment.isMoment(durationEndTime) && (
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangeSeconds(TimeUpdateAction.SUBTRACT);
                }}
                disabled={durationEndTime
                  .clone()
                  .subtract(1, 'second')
                  .isSameOrBefore(durationStartTime)}
              >
                <KeyboardArrowDown />
              </IconButton>
            )}
          </Box>

          {/* Delete Button */}
          {isEditing && (
            <Box sx={{ borderLeft: `1px solid ${FORTA_GRAY3}`, pl: 0.5 }}>
              <IconButton
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
              >
                <TrashIcon width={21} height={21} />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <FHConfirmDialog
        open={showDeleteDialog}
        title={'Delete time entry'}
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={6}>
            <Typography textAlign="center" fontSize={20} fontWeight={500}>
              Are you sure you want to delete this time duration entry?
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={500} mt={1} mb={1}>
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext={'DELETE'}
        canceltext={'CANCEL'}
        onClose={() => setShowDeleteDialog(false)}
        onSubmit={() => {
          setShowDeleteDialog(false);
          onDelete(id);
        }}
      />
    </>
  );
}

export default FHDurationTag;
